import React, { useState, useEffect } from "react";
import { Autocomplete, TextField, Checkbox } from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { handleTrainPredictor } from "../../modules/predictorModule.ts";
import {toast, ToastContainer} from 'react-toastify';import Button from "../../components/common/Button.tsx";
import { useNavigate } from "react-router-dom";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { LabelText } from "../../components/common/Labeltext.tsx";
import { Input, Row, Table } from "reactstrap";
import SelectField from "../../components/common/SelectField.tsx";
import { useSSE } from "../../context/SSEContext.tsx";
interface DatasetSchema {
  column_name: string;
  data_type: string;
}

interface PredictorData {
  dataset_id: number;
  predictor_name: string;
  domain: string;
  problem_type: string;
  dataset_schema: DatasetSchema[];
  attributes: string[];
  is_incremental: boolean;
  shap_enabled: boolean;
  tune_enabled: boolean;
  zero_fill_enabled: boolean;
  frequency: string;
}

interface DatasetHeader {
  column_name: string;
  column_type: string;
  attribute_name: string;
  selected: boolean;
  agg_fn: string;
}

const TrainPredictor: React.FC = () => {
  const [datasetHeaders, setDatasetHeaders] = useState<DatasetHeader[]>([]);
  const navigate = useNavigate();
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<number, string>
  >({});
  const [availableAttributes, setAvailableAttributes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  //const toast = useToast();
  const { subscribeJob } = useSSE();
  

  useEffect(() => {
    try {
      const storedResponse = localStorage.getItem("predictorResponse");
      const predictorData = storedResponse ? JSON.parse(storedResponse) : null;

      const headers: DatasetHeader[] = predictorData.dataset_schema.map(
        (item) => ({
          column_name: item.column_name,
          column_type: mapDataType(item.data_type),
          attribute_name: "",
          selected: false,
          agg_fn: ""
        })
      );

      setDatasetHeaders(headers);
      setAvailableAttributes(predictorData.attributes || ["Id", "Churn"]);
    } catch (error) {
      console.error("Error parsing session storage data:", error);
    }
  }, []);

  const mapDataType = (dataType: string): string => {
    const typeMap: Record<string, string> = {
      Integer: "Integer",
      String: "String",
      Boolean: "Boolean",
      Float: "Float",
      DateTime: "DateTime",
    };
    return typeMap[dataType] || dataType;
  };

  const handleCheckboxChange = (index: number): void => {
    const updatedHeaders = [...datasetHeaders];
    updatedHeaders[index].selected = !updatedHeaders[index].selected;
    updatedHeaders[index].agg_fn = { label: "", value: "" };
    setDatasetHeaders(updatedHeaders);
  };

  const handleAggregateChange = (index: number, value: string): void => {
    const updatedHeaders = [...datasetHeaders];
    updatedHeaders[index].agg_fn = value;
    setDatasetHeaders(updatedHeaders);
  }

  const handleAttributeChange = (
    index: number,
    newValue: string | null
  ): void => {
    setSelectedAttributes((prev) => {
      const updatedAttributes = { ...prev };

      delete updatedAttributes[index];

      if (newValue) {
        updatedAttributes[index] = newValue;
      }

      return updatedAttributes;
    });

    setDatasetHeaders((prevHeaders) => {
      const updatedHeaders = [...prevHeaders];
      updatedHeaders[index].attribute_name = newValue || "";
      updatedHeaders[index].selected = !!newValue;

      return updatedHeaders;
    });
  };

  const getFilteredAttributes = (index: number): string[] => {
    return availableAttributes.filter((attr) => {
      if (selectedAttributes[index] === attr) return true;
      return !Object.values(selectedAttributes).includes(attr);
    });
  };

  const aggregateOptions = {
    "Sum": "sum",
    "Mean": "mean",
    "Min": "min",
    "Max": "max",
    "First": "first",
    "Last": "last"
  }

  const aggregateDisplayOptions = Object.keys(aggregateOptions);

  const handleTrain = async () => {
    setLoading(true);

    try {
      const selectedColumns = datasetHeaders
        .filter((header) => header.selected)
        .map(({ column_name, column_type, attribute_name, agg_fn }) => ({
          column_name,
          data_type: column_type,
          attribute_name,
          agg_fn: aggregateOptions[agg_fn.value],
        }));

      if (selectedColumns.length === 1) {
        toast.error("Please select two attribute.");
        setLoading(false);
        return;
      }

      const storedResponse = localStorage.getItem("predictorResponse");
      if (!storedResponse) {
        toast.error("Predictor data not found. Please try again.");
        setLoading(false);
        return;
      }

      const predictorData: PredictorData = JSON.parse(storedResponse);

      const payload = {
        dataset_id: predictorData.dataset_id,
        predictor_name: predictorData.predictor_name,
        domain: predictorData.domain,
        problem_type: predictorData.problem_type,
        columns: selectedColumns,
        is_incremental: predictorData.is_incremental || false,
        shap_enabled: predictorData.shap_enabled || false,
        tune_enabled: predictorData.tune_enabled || false,
        zero_fill_enabled: predictorData.zero_fill_enabled || false,
        ...(predictorData.problem_type.toLowerCase() === "forecast" && { frequency: predictorData?.frequency })
      };

      const resp = await handleTrainPredictor(
        payload.dataset_id,
        payload.predictor_name,
        payload.domain,
        payload.problem_type,
        payload.columns,
        payload.is_incremental,
        payload.shap_enabled,
        payload.tune_enabled,
        payload.zero_fill_enabled,
        payload.problem_type === "Forecast" ? payload.frequency : "",
      );
      sessionStorage.removeItem("createPredictorForm");
      localStorage.removeItem("predictorResponse");
      subscribeJob("Training", resp.data.id);

      toast.success(resp.message || "Training Initiated Successfully");
      navigate("/TrainingModal");
    } catch (error) {
      console.error("Error starting training:", error);

      const errorMessage =
        error.response?.data?.detail ||
        "An unexpected error occurred while starting training.";


      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/CreatePredictor");
  };

  const storedResponse = localStorage.getItem("predictorResponse");
  const predictorData = storedResponse ? JSON.parse(storedResponse) : null;

  return (
    <>
      <BreadCrumb moduleName={"Training"} pageName={"Create Predictor"} />
      <ToastContainer position="top-center" />
      <div className="listingjs-table " id="customerList">
        <div className="live-preview">
          <div className="align-items-center d-flex mb-3 ">
            <h4 className="card-title mb-0 flex-grow-1">Manage Dataset Headers</h4>
          </div>
          <div className="border table-border p-3">
            <div className=''>
              <LabelText
                htmlFor={"DatasetHeaders"}
                className={"text-danger"}
                displayText={"Dataset Headers"}
                important={"*"}
              />
              <LabelText
                htmlFor={"DatasetHeadersPlaceholder"}
                classLabelName={`d-block ms-0 rv-sub-label rv-label-muted`}
                displayText={"Map Attribute names to Data headers for accurate data processing and training"}
              />
            </div>
            <Table className="table-borderless align-middle table-nowrap ms-2 mb-0 overflow-auto border-seperate">
              <thead>
                <tr>
                  <th className="px-0 mx-0" scope="col"></th>
                  <th className={`${predictorData?.problem_type === "Forecast" ? "w-25" : "w-33"} ms-2`} scope="col">Data Headers Name</th>
                  <th className={`${predictorData?.problem_type === "Forecast" ? "w-25" : "w-33"} ms-2`} scope="col">Headers Type</th>
                  <th className={`${predictorData?.problem_type === "Forecast" ? "w-25" : "w-33"} ms-2`} scope="col">Attribute Name</th>
                  {predictorData?.problem_type === "Forecast" && <th className="ms-2 w-25" scope="col">Aggregate Function</th>}
                </tr>
              </thead>
              <tbody>
                {datasetHeaders && (
                  datasetHeaders.map((header, index) => (
                    (
                      <tr key={index} className="ms-3">
                        <td >
                          <Input
                            type="checkbox"
                            className='rv-table-checkbox'
                            checked={header.selected}
                            style={{ borderWidth: '2px', borderColor: '#000', borderRadius: '0' }}
                            onClick={() => handleCheckboxChange(index)}
                          />
                        </td>
                        <td className={`${predictorData?.problem_type === "Forecast" ? "w-25" : "w-33"} ms-2`} >
                          <Input
                            type="text"
                            value={header.column_name}
                            disabled={true}
                          />
                        </td>
                        <td className={`${predictorData?.problem_type === "Forecast" ? "w-25" : "w-33"} ms-2`} >
                          <Input
                            type="text"
                            value={header.column_type}
                            disabled={true}
                          />
                        </td>
                        <td className={`${predictorData?.problem_type === "Forecast" ? "w-25" : "w-33"} ms-2`} >
                          <SelectField
                            key={`attr-${index}-${header.selected}-${header.column_name}`}
                            className=""
                            value={
                              getFilteredAttributes(index)
                                .map(opt => ({ label: opt, value: opt }))
                                .find(opt => opt.value === header.attribute_name) || null
                            }
                            placeholder=" "
                            handleChange={(selectedOption) => {
                              handleAttributeChange(index, selectedOption?.value || "");
                            }}
                            options={getFilteredAttributes(index).map(opt => ({
                              label: opt,
                              value: opt
                            }))}
                            isRVStyle={true}
                            isClearable={true}
                          />
                        </td>
                        {predictorData?.problem_type === "Forecast" &&
                          <td className="w-30 ms-2">
                            <SelectField
                              key={`attr-${index}-${header.selected}-${header.column_name}`}
                              className={""}
                              isDisabled={!header.selected || !!header.attribute_name}
                              value={aggregateDisplayOptions.find(opt => opt === header.agg_fn)}
                              placeholder={" "}
                              handleChange={(selectedOption) => {
                                handleAggregateChange(index, selectedOption as string)
                              }}
                              options={aggregateDisplayOptions.map((opt) => ({
                                label: opt, value: opt
                              }))}
                              isRVStyle={true}
                            />
                          </td>}
                      </tr>
                    )
                  ))
                )}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
      <Row lg={6} className="justify-content-end mt-4">
        <div className="live-preview">
          <div className="d-flex justify-content-end gap-2 mb-5">
            <button
              type="button"
              className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-md-auto text-nowrap btn btn-secondary"
              onClick={handleCancel}
            >
              <i className="ri-close-line label-icon secondary-label-icon align-middle fs-16 me-2"></i>
              <span className="px-2 ms-2 me-1 fw-bold">
                Cancel
              </span>
            </button>
            <button
              onClick={handleTrain}
              type="submit"
              className="btn-label rv-primary-btn rounded-1 btn btn-secondary d-flex align-items-center"
              style={{ height: 'auto', minHeight: 'fit-content' }}
            >
              <i className="ri-play-fill label-icon align-middle fs-16 me-1"></i>
              <span className="ms-2 me-1 fw-bold d-inline-flex align-items-center gap-2">
                {loading ? (
                  <>
                    <span>Training</span>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                  </>
                ) : (
                  "Train"
                )}
              </span>
            </button>
          </div>
        </div>
      </Row>
    </>
  );
};

export default TrainPredictor;
